import React, { useState, useEffect } from "react";
import { ShowButton, EditButton, DeleteButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Table, Space, Card, Input } from "antd";
import { SearchOutlined, PlusOutlined, BankOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";

interface Branch {
    id: string;
    name: string;
    code: string;
    email?: string;
    phoneNumber?: string;
    address?: string;
    createdAt: string;
}

export const BranchList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<Branch[]>([]);
    const navigate = useNavigate();

    const { data, isLoading } = useList({
        resource: "branch",
    });

    useEffect(() => {
        if (!data?.data) return;

        let branches = data.data as Branch[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            branches = branches.filter((branch: Branch) =>
                branch.name?.toLowerCase().includes(searchLower) ||
                branch.email?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...branches].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value}</span>
            ),
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
        },
        {
            title: "Phone",
            dataIndex: "phoneNumber",
            key: "phoneNumber",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['lg'] as any,
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_: any, record: Branch) => (
                <Space size="small">
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    <EditButton hideText size="small" recordItemId={record.id} />
                    <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Branches"
                icon={<BankOutlined />}
                breadcrumbs={[{ title: "Branches" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/branch/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by name or email"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 600 }}
                    />
                </Card>
            </div>
        </div>
    );
};
